import { encode as base64Encode } from "js-base64";
import AppCache from "../../Helper/Common/AppCache";
import { decryptTheParams, encryptTheParams } from "../../Helper/QueryParams/EncryptDecrypt";

/**
 * Routes whose filter selection is their own.
 *
 * The custom dashboard is a separate platform with its own filter config, so a
 * selection handed over from a standard page must not apply there. Derived from
 * the path rather than asked for per call site, because an opt-out a page has
 * to remember is one a page can forget.
 */
export const ISOLATED_FILTER_ROUTE_PREFIX = "/custom-observability";

export const isIsolatedFilterRoute = (pathname = "") =>
  String(pathname || "").startsWith(ISOLATED_FILTER_ROUTE_PREFIX);

/**
 * The global filter selection, encoded onto an outbound link.
 *
 * The in-app selection lives in a React context, which is per tab - and the
 * Overview cards link with target="_blank", so the page that opens is a fresh
 * app instance that can never see it. The link has to carry the selection
 * itself or it arrives unfiltered, which is what it used to do.
 *
 * The handoff is consumed once: whoever reads it strips it from the URL, so
 * reloading the page starts clean rather than re-applying it. The link carries
 * the filter to the page it opens, and no further.
 */
export const encodeGlobalFilterForUrl = (globalFilters = {}) => {
  const fleetIds = globalFilters?.fleets;
  const hierarchicalLocation = globalFilters?.hierarchicalLocation;
  if (!fleetIds?.length && !hierarchicalLocation?.length) return null;
  return JSON.stringify({
    fleetIds: fleetIds?.length ? fleetIds : null,
    hierarchicalLocation: hierarchicalLocation?.length ? hierarchicalLocation : null,
  });
};

/**
 * The selection handed over by the link that opened this page, or null.
 *
 * Malformed input returns null rather than throwing: the parameter is in a URL
 * a user can edit or truncate, and an unreadable filter must leave the page
 * unfiltered rather than broken.
 */
/**
 * Remove the handoff from the address bar, keeping every other parameter.
 *
 * Called once the selection has been read. Without it the parameter would still
 * be in the URL on reload and the filter would come back, which is the one thing
 * a reload is meant not to do.
 */
export const stripGlobalFilterFromUrl = () => {
  try {
    const params = decryptTheParams();
    if (!params?.globalFilter) return;
    delete params.globalFilter;
    const hash = Object.keys(params).length ? encryptTheParams(params) : "";
    window.history.replaceState(
      null,
      "",
      hash ? `${window.location.pathname}?${hash}` : window.location.pathname
    );
  } catch {
    // An address bar we cannot rewrite is not worth failing a page render over.
  }
};

export const readGlobalFilterFromUrl = () => {
  try {
    const raw = decryptTheParams()?.globalFilter;
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const fleetIds = Array.isArray(parsed?.fleetIds) ? parsed.fleetIds : null;
    const hierarchicalLocation = Array.isArray(parsed?.hierarchicalLocation)
      ? parsed.hierarchicalLocation
      : null;
    if (!fleetIds && !hierarchicalLocation) return null;
    return { fleetIds, hierarchicalLocation };
  } catch {
    return null;
  }
};

export const getInventoryFilterUrl = (
  globalFilters,
  localFilters,
  sorting = {},
  basePath = '/inventory'
) => {
  let globalFiltersOutput = {};
  if (Object.keys(globalFilters).length) {
    globalFiltersOutput = handleGlobalFiltersForInventoryUrl(globalFilters);
  }

  const filtersEncoded = encodeURIComponent(
    JSON.stringify({ ...globalFiltersOutput, ...localFilters })
  );
  const sortingEncoded = encodeURIComponent(JSON.stringify(sorting));
  const globalFilter = encodeGlobalFilterForUrl(globalFilters);
  const base64 = base64Encode(
    `filters=${filtersEncoded}&sorting=${sortingEncoded}` +
      (globalFilter ? `&globalFilter=${encodeURIComponent(globalFilter)}` : "")
  );
  return `${basePath}?${base64}`;
};

export const handleGlobalFiltersForInventoryUrl = (globalFilters) => {
  const { onboardingLocations = {}, allOnboardingLocations = {} } =
    globalFilters;

  let selectedCountries = onboardingLocations?.countries;
  let selectedStates = onboardingLocations?.states;
  let selectedCities = onboardingLocations?.cities;
  let selectedDepots = onboardingLocations?.depots;

  if (
    (allOnboardingLocations?.countries || []).length ===
    (onboardingLocations?.countries || []).length
  ) {
    selectedCountries = [];
  }
  if (
    (allOnboardingLocations?.states || []).length ===
    (onboardingLocations?.states || []).length
  ) {
    selectedStates = [];
  }
  if (
    (allOnboardingLocations?.cities || []).length ===
    (onboardingLocations?.cities || []).length
  ) {
    selectedCities = [];
  }
  if (
    (allOnboardingLocations?.depots || []).length ===
    (onboardingLocations?.depots || []).length
  ) {
    selectedDepots = [];
  }

  return {
    ...(selectedCountries.length > 0 && { originCountry: selectedCountries }),
    ...(selectedStates.length > 0 && { originState: selectedStates }),
    ...(selectedCities.length > 0 && { originCity: selectedCities }),
    ...(selectedDepots.length > 0 && { originDepo: selectedDepots }),
  };
};

export const getSelectedLocationsDisplayLabel = (
  selectedOptions,
  allOptions,
  label
) => {
  let displayLabel = "";
  let tooltipLabel = "";

  if (selectedOptions.length === allOptions.length) {
    displayLabel = "All Selected";
    tooltipLabel = "All Selected";
    if (label.toLowerCase() === "country") {
      displayLabel = "All Countries";
      tooltipLabel = "All Countries";
    }
    if (label.toLowerCase() === "state") {
      displayLabel = "All States";
      tooltipLabel = "All States";
    }
    if (label.toLowerCase() === "city") {
      displayLabel = "All Cities";
      tooltipLabel = "All Cities";
    }
    if (label.toLowerCase() === "depot") {
      displayLabel = "All Depots";
      tooltipLabel = "All Depots";
    }
  } else if (selectedOptions.length > 0) {
    displayLabel = `${selectedOptions[0]}`;

    if (selectedOptions.length > 1) {
      displayLabel += ` + ${selectedOptions.length - 1}`;
    }

    tooltipLabel = selectedOptions.join(", ");
  }

  return { displayLabel, tooltipLabel };
};

export const handleGlobalFiltersForInventoryUrlOutdated = (globalFilters) => {
  const { onboardingLocations = [], allOnboardingLocations = [] } =
    globalFilters;
  if (!onboardingLocations.length || !allOnboardingLocations.length) return {};

  const selectedCountries = [];
  const selectedStates = [];
  const selectedCities = [];
  const selectedDepots = [];

  // Multiset equality (handles duplicates)
  const arraysEqual = (a = [], b = []) => {
    if (a.length !== b.length) return false;
    const counts = {};
    for (const v of a) counts[v] = (counts[v] || 0) + 1;
    for (const v of b) {
      if (!counts[v]) return false;
      counts[v]--;
    }
    return Object.values(counts).every((c) => c === 0);
  };

  // Build lookup: country -> state -> city -> depots[]
  const onboardingMap = {};
  onboardingLocations.forEach((country) => {
    onboardingMap[country.country] = (country.states || []).reduce(
      (sAcc, state) => {
        sAcc[state.state] = (state.cities || []).reduce((cAcc, city) => {
          cAcc[city.name] = city.depots ? [...city.depots] : []; // copy to be safe
          return cAcc;
        }, {});
        return sAcc;
      },
      {}
    );
  });

  // Helpers
  const isCityFullySelected = (cityObj, selectedCityDepots = []) =>
    arraysEqual(cityObj.depots || [], selectedCityDepots || []);

  const isStateFullySelected = (stateObj, selectedStateObj = {}) => {
    if (!selectedStateObj) return false;
    return (stateObj.cities || []).every((cityObj) => {
      const cityName = cityObj.name;
      // city must exist in selectedStateObj (presence check respects empty arrays)
      if (!Object.prototype.hasOwnProperty.call(selectedStateObj, cityName))
        return false;
      return isCityFullySelected(cityObj, selectedStateObj[cityName]);
    });
  };

  const isCountryFullySelected = (countryObj, selectedCountryObj = {}) => {
    if (!selectedCountryObj) return false;
    return (countryObj.states || []).every((stateObj) => {
      const stateName = stateObj.state;
      if (!Object.prototype.hasOwnProperty.call(selectedCountryObj, stateName))
        return false;
      return isStateFullySelected(stateObj, selectedCountryObj[stateName]);
    });
  };

  // Traverse allOnboardingLocations
  allOnboardingLocations.forEach((country) => {
    const countryName = country.country;
    const selectedCountry = onboardingMap[countryName];
    if (!selectedCountry) return; // country not selected at all

    // keep country if any selection exists (same behavior as your original)
    selectedCountries.push(countryName);

    // if country fully selected -> skip deeper hierarchy
    if (isCountryFullySelected(country, selectedCountry)) {
      return;
    }

    (country.states || []).forEach((stateObj) => {
      const stateName = stateObj.state;
      const selectedStateObj = selectedCountry[stateName];
      if (!selectedStateObj) return; // state not selected at all

      // record state presence
      selectedStates.push(stateName);

      // if state fully selected -> skip deeper hierarchy
      if (isStateFullySelected(stateObj, selectedStateObj)) {
        return;
      }

      // else traverse cities
      (stateObj.cities || []).forEach((cityObj) => {
        const cityName = cityObj.name;
        // presence check so empty arrays (selected but no depots) are respected
        if (!Object.prototype.hasOwnProperty.call(selectedStateObj, cityName))
          return;

        // record city presence
        selectedCities.push(cityName);

        const selectedCityDepots = selectedStateObj[cityName] || [];
        // if city fully selected -> skip depots
        if (isCityFullySelected(cityObj, selectedCityDepots)) {
          return;
        }

        // else partial depot selection -> add selected depots
        (selectedCityDepots || []).forEach((d) => selectedDepots.push(d));
      });
    });
  });

  return {
    ...(selectedCountries.length > 0 && { originCountry: selectedCountries }),
    ...(selectedStates.length > 0 && { originState: selectedStates }),
    ...(selectedCities.length > 0 && { originCity: selectedCities }),
    ...(selectedDepots.length > 0 && { originDepo: selectedDepots }),
  };
};

export const getSelectedLocationsDisplayLabelOutdated = (globalFilters) => {
  const { onboardingLocations = [], allOnboardingLocations = [] } =
    globalFilters;

  // helper to check equality of arrays ignoring order
  const arraysEqual = (a = [], b = []) => {
    if (a.length !== b.length) return false;
    const setB = new Set(b);
    return a.every((v) => setB.has(v));
  };

  // formatting helper
  const formatLabel = (items, level) => {
    if (!items || items.length === 0) return null;

    const first = items[0];
    const count = items.length - 1;
    const label = count > 0 ? `${first} + ${count}` : `${first}`;
    const tooltip = `${level}: ${items.join(", ")}`;
    return { displayLabel: label, tooltipLabel: tooltip, level };
  };

  // derive full lists for comparison
  const fullCountries = allOnboardingLocations.map((c) => c.country);
  const fullStates = allOnboardingLocations.flatMap((c) =>
    c.states.map((s) => s.state)
  );
  const fullCities = allOnboardingLocations.flatMap((c) =>
    c.states.flatMap((s) => s.cities.map((ct) => ct.name))
  );
  const fullDepots = allOnboardingLocations.flatMap((c) =>
    c.states.flatMap((s) => s.cities.flatMap((ct) => ct.depots || []))
  );

  // derive actual selected lists
  const selectedCountries = onboardingLocations.map((c) => c.country);
  const selectedStates = onboardingLocations.flatMap((c) =>
    (c.states || []).map((s) => s.state)
  );
  const selectedCities = onboardingLocations.flatMap((c) =>
    (c.states || []).flatMap((s) => (s.cities || []).map((ct) => ct.name))
  );
  const selectedDepots = onboardingLocations.flatMap((c) =>
    (c.states || []).flatMap((s) =>
      (s.cities || []).flatMap((ct) => ct.depots || [])
    )
  );

  const filters = handleGlobalFiltersForInventoryUrlOutdated(globalFilters);

  // depot level
  if (filters.originDepo && filters.originDepo.length > 0) {
    if (arraysEqual(selectedDepots, fullDepots)) {
      return {
        displayLabel: "All Depots",
        tooltipLabel: "Depot: All",
        level: "Depot",
      };
    }
    return formatLabel(selectedDepots, "Depot");
  }

  // city level
  if (filters.originCity && filters.originCity.length > 0) {
    if (arraysEqual(selectedCities, fullCities)) {
      return {
        displayLabel: "All Cities",
        tooltipLabel: "City: All",
        level: "City",
      };
    }
    return formatLabel(selectedCities, "City");
  }

  // state level
  if (filters.originState && filters.originState.length > 0) {
    if (arraysEqual(selectedStates, fullStates)) {
      return {
        displayLabel: "All States",
        tooltipLabel: "State: All",
        level: "State",
      };
    }
    return formatLabel(selectedStates, "State");
  }

  // country level
  if (filters.originCountry && filters.originCountry.length > 0) {
    if (arraysEqual(selectedCountries, fullCountries)) {
      return {
        displayLabel: "All Countries",
        tooltipLabel: "Country: All",
        level: "Country",
      };
    }
    return formatLabel(selectedCountries, "Country");
  }

  // fallback
  return { displayLabel: "", tooltipLabel: "", level: null };
};

export const prepareHeadersForGlobalFilters = (globalFilters, module = null) => {
  const headers = {
    "Content-Type": "application/json",
  };

  headers["excludeNotDefinedOriginDepot"] = btoa(true);

  if (globalFilters.fleets && globalFilters.fleets.length) {
    headers["fleetIDs"] = btoa(JSON.stringify(globalFilters.fleets));
  }

  if (
    globalFilters.onboardingLocations &&
    Object.keys(globalFilters.onboardingLocations).length
  ) {
    const selectedCountryList =
      globalFilters.onboardingLocations?.countries || [];
    const selectedStateList = globalFilters.onboardingLocations?.states || [];
    const selectedCityList = globalFilters.onboardingLocations?.cities || [];
    const selectedDepotList = globalFilters.onboardingLocations?.depots || [];

    const allCountryList =
      globalFilters?.allOnboardingLocations?.countries || [];
    const allStateList = globalFilters?.allOnboardingLocations?.states || [];
    const allCityList = globalFilters?.allOnboardingLocations?.cities || [];
    const allDepotList = globalFilters?.allOnboardingLocations?.depots || [];

    const onboardingLocations = JSON.stringify({
      // originCountry: selectedCountryList.length === allCountryList.length ? [] : selectedCountryList,
      // originState: selectedStateList.length === allStateList.length ? [] : selectedStateList,
      // originCity: selectedCityList.length === allCityList.length ? [] : selectedCityList,
      originDepo:
        selectedDepotList.length === allDepotList.length
          ? []
          : selectedDepotList,
    });
    headers["originLocations"] = btoa(onboardingLocations);
  }

  // Add hierarchical location filter for analytics
  if (
    globalFilters.hierarchicalLocation &&
    globalFilters.hierarchicalLocation.length > 0
  ) {
    headers["originGroupedLocations"] = btoa(
      JSON.stringify(globalFilters.hierarchicalLocation)
    );
  }

  if (globalFilters.alertCategories && globalFilters.alertCategories.length) {
    headers["bmsAlertCategories"] = btoa(
      JSON.stringify(globalFilters.alertCategories)
    );
    if (module === "smartAlerts") {
      headers["alertTypes"] = btoa(JSON.stringify(globalFilters.alertCategories));
    }
  }

  // Add operational status filter for all tabs
  if (globalFilters.operationalStatus && globalFilters.operationalStatus.length > 0) {
    // Always send the selected operational statuses (never send empty array)
    // Default: ["operational", "non_operational"]
    // User can change via BMS Faults page UI
    headers["operationalStatus"] = btoa(JSON.stringify(globalFilters.operationalStatus));
  }

  // Add configuration filter (Battery Intelligence)
  if (globalFilters.configuration && globalFilters.configuration.length > 0) {
    headers["configurationIDs"] = btoa(JSON.stringify(globalFilters.configuration));
  }

  // Exclude specific operational status values based on org meta globalConfig.preAppliedGlobalFilters.
  // Applied unconditionally so the header is sent even when no module argument is provided.
  {
    const preAppliedGlobalFilters = AppCache.getItem("preAppliedGlobalFilters") || {};
    const moduleFilters = (module ? preAppliedGlobalFilters[module] : preAppliedGlobalFilters) || {};
    if (moduleFilters.excludeOperationalStatus?.length > 0) {
      headers["excludeOperationalStatus"] = btoa(JSON.stringify(moduleFilters.excludeOperationalStatus));
    }
  }

  return headers;
};

export const getBmsFaultsUrl = (
  globalFilters,
  localFilters,
  sort = {},
  tab = "active"
) => {
  let globalFiltersOutput = {};
  if (Object.keys(globalFilters).length) {
    globalFiltersOutput = handleGlobalFiltersForInventoryUrl(globalFilters);
  }

  const filtersEncoded = encodeURIComponent(
    JSON.stringify({ ...globalFiltersOutput, ...localFilters })
  );

  const sortEncoded = encodeURIComponent(JSON.stringify(sort));
  const tabEncoded = encodeURIComponent(JSON.stringify(tab));
  const globalFilter = encodeGlobalFilterForUrl(globalFilters);

  const base64 = base64Encode(
    `filters=${filtersEncoded}&tab=${tabEncoded}&sort=${sortEncoded}` +
      (globalFilter ? `&globalFilter=${encodeURIComponent(globalFilter)}` : "")
  );
  return `/bms-faults?${base64}`;
};

export const getCustomBmsFaultsUrl = (
  globalFilters,
  localFilters,
  sort = {},
  tab = "activeFaults"
) => {
  let globalFiltersOutput = {};
  if (Object.keys(globalFilters).length) {
    globalFiltersOutput = handleGlobalFiltersForInventoryUrl(globalFilters);
  }
  const filtersEncoded = encodeURIComponent(
    JSON.stringify({ ...globalFiltersOutput, ...localFilters })
  );
  const sortEncoded = encodeURIComponent(JSON.stringify(sort));
  const tabEncoded = encodeURIComponent(JSON.stringify(tab));
  const base64 = base64Encode(
    `filters=${filtersEncoded}&tab=${tabEncoded}&sort=${sortEncoded}`
  );
  return `/custom-observability/bms-faults?${base64}`;
};

/**
 * Transform API location data to OnboardingLocationFilter format for hierarchy
 * @param {Array} apiData - Array of vehicle location objects from API
 * @returns {Array} - Hierarchical location structure (Country > State > City > Depots)
 */
export const transformOnboardingLocationsForHierarchy = (apiData) => {
  if (!apiData || !Array.isArray(apiData)) {
    return [];
  }

  // Helper to normalize strings (capitalize first letter, handle null/undefined)
  const normalize = (str) => {
    if (!str) return null;
    const trimmed = str.trim();
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
  };

  // Build nested structure using Map for efficient lookups
  const countriesMap = new Map();

  apiData.forEach((item) => {
    const { originCountry, originState, originCity, originDepo } = item;

    // Skip entries with null country/state/city
    if (!originCountry || !originState || !originCity) {
      return;
    }

    const country = normalize(originCountry);
    const state = normalize(originState);
    const city = normalize(originCity);
    const depot = originDepo ? originDepo.trim() : null;

    // Get or create country
    if (!countriesMap.has(country)) {
      countriesMap.set(country, new Map());
    }
    const statesMap = countriesMap.get(country);

    // Get or create state
    if (!statesMap.has(state)) {
      statesMap.set(state, new Map());
    }
    const citiesMap = statesMap.get(state);

    // Get or create city
    if (!citiesMap.has(city)) {
      citiesMap.set(city, new Set());
    }
    const depotsSet = citiesMap.get(city);

    // Add depot if it exists
    if (depot) {
      depotsSet.add(depot);
    }
  });

  // Convert Maps and Sets to the required array structure
  const result = [];

  countriesMap.forEach((statesMap, countryName) => {
    const states = [];

    statesMap.forEach((citiesMap, stateName) => {
      const cities = [];

      citiesMap.forEach((depotsSet, cityName) => {
        cities.push({
          name: cityName,
          depots: Array.from(depotsSet).sort(),
        });
      });

      // Sort cities by name
      cities.sort((a, b) => a.name.localeCompare(b.name));

      states.push({
        state: stateName,
        cities,
      });
    });

    // Sort states by name
    states.sort((a, b) => a.state.localeCompare(b.state));

    result.push({
      country: countryName,
      states,
    });
  });

  // Sort countries by name
  result.sort((a, b) => a.country.localeCompare(b.country));

  return result;
};
